import { useEffect, useRef, useState } from 'react';
import { CaretDown, Check } from 'shared/icons';
import { useTokens } from './tokens';

function IconList() {
    return (
        <svg width="18" height="14" viewBox="0 0 18 14" fill="none" aria-hidden="true">
            <rect x=".5" y=".5" width="17" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
            <line x1="1" y1="5" x2="17" y2="5" stroke="currentColor" strokeWidth="1" />
            <line x1="1" y1="9" x2="17" y2="9" stroke="currentColor" strokeWidth="1" />
        </svg>
    );
}
function IconVertical() {
    return (
        <svg width="18" height="14" viewBox="0 0 18 14" fill="none" aria-hidden="true">
            <rect x=".5" y=".5" width="10" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
            <rect x="12.5" y=".5" width="5" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
        </svg>
    );
}
function IconHorizontal() {
    return (
        <svg width="18" height="14" viewBox="0 0 18 14" fill="none" aria-hidden="true">
            <rect x=".5" y=".5" width="17" height="7.5" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
            <rect x=".5" y="10" width="17" height="3.5" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
        </svg>
    );
}
function IconOverlay() {
    return (
        <svg width="18" height="14" viewBox="0 0 18 14" fill="none" aria-hidden="true">
            <rect x=".5" y=".5" width="17" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
            <rect
                x="9.5"
                y="1.5"
                width="7"
                height="11"
                rx="1"
                fill="currentColor"
                fillOpacity=".15"
                stroke="currentColor"
                strokeWidth="1"
            />
        </svg>
    );
}
function IconModal() {
    return (
        <svg width="18" height="14" viewBox="0 0 18 14" fill="none" aria-hidden="true">
            <rect x=".5" y=".5" width="17" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.2" opacity=".35" />
            <rect
                x="3.5"
                y="2.5"
                width="11"
                height="9"
                rx="1.5"
                fill="currentColor"
                fillOpacity=".12"
                stroke="currentColor"
                strokeWidth="1.2"
            />
        </svg>
    );
}

export const LAYOUT_MODES = [
    { id: 'list', label: 'Página única', hint: 'Clica numa linha → painel fullscreen', Icon: IconList },
    { id: 'modal', label: 'Modal', hint: 'Detalhe abre em popup centralizado', Icon: IconModal },
    { id: 'vertical', label: 'Split vertical', hint: 'Detalhe na lateral direita', Icon: IconVertical },
    { id: 'horizontal', label: 'Split horizontal', hint: 'Detalhe na parte inferior', Icon: IconHorizontal },
    { id: 'overlay', label: 'Drawer lateral', hint: 'Painel flutua sobre a tabela', Icon: IconOverlay },
];

export function LayoutToggle({ mode, onChange }) {
    const T = useTokens();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) {
            return;
        }
        const h = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [open]);

    const cycle = () => {
        const idx = LAYOUT_MODES.findIndex((m) => m.id === mode);
        onChange(LAYOUT_MODES[(idx + 1) % LAYOUT_MODES.length].id);
    };

    const cur = LAYOUT_MODES.find((m) => m.id === mode) || LAYOUT_MODES[2];
    const btnBase = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 32,
        background: T.bg,
        border: `1px solid ${T.line}`,
        cursor: 'pointer',
        color: T.inkMid,
        transition: 'background 0.1s',
        outline: 'none',
    };

    return (
        <div ref={ref} style={{ position: 'relative', display: 'flex' }}>
            <button
                type="button"
                title={`Layout: ${cur.label} (clique para ciclar)`}
                onClick={cycle}
                style={{ ...btnBase, width: 36, borderRadius: '4px 0 0 4px', borderRight: 'none' }}
            >
                <cur.Icon />
            </button>
            <button
                type="button"
                title="Escolher layout"
                onClick={() => setOpen((o) => !o)}
                style={{ ...btnBase, width: 20, borderRadius: '0 4px 4px 0', color: T.muted }}
            >
                <CaretDown size={11} />
            </button>

            {open && (
                <div
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        right: 0,
                        zIndex: 300,
                        background: T.bg,
                        border: `1px solid ${T.line}`,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        borderRadius: 6,
                        width: 260,
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            padding: '8px 14px 6px',
                            fontSize: 10,
                            color: T.muted,
                            textTransform: 'uppercase',
                            letterSpacing: 0.6,
                            borderBottom: `1px solid ${T.line}`,
                        }}
                    >
                        Modo de exibição
                    </div>
                    {LAYOUT_MODES.map((m) => {
                        const active = m.id === mode;
                        return (
                            <button
                                key={m.id}
                                type="button"
                                onClick={() => {
                                    onChange(m.id);
                                    setOpen(false);
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    width: '100%',
                                    padding: '10px 14px',
                                    cursor: 'pointer',
                                    background: active ? T.bgMid : 'transparent',
                                    border: 'none',
                                    borderLeft: active ? `2px solid ${T.ink}` : '2px solid transparent',
                                    color: T.ink,
                                    textAlign: 'left',
                                    outline: 'none',
                                }}
                            >
                                <span style={{ color: T.muted, display: 'flex', alignItems: 'center' }}>
                                    <m.Icon />
                                </span>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 13, fontWeight: active ? 600 : 400 }}>{m.label}</div>
                                    <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{m.hint}</div>
                                </div>
                                {active && <Check size={14} color={T.ink} />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
